<template>
  <el-dialog
    :title="mode === 'add' ? '新建价目表' : '编辑价目表'"
    :visible.sync="dialogVisible"
    width="640px"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="130px" v-loading="submitting">
      <el-form-item label="价目表名称" prop="name">
        <el-input v-model="form.name" placeholder="请输入价目表名称" maxlength="50" />
      </el-form-item>
      <el-form-item label="项目类型" prop="projectType">
        <el-select
          v-model="form.projectType"
          placeholder="请选择项目类型"
          style="width: 100%"
          :disabled="mode === 'edit'"
        >
          <el-option
            v-for="item in projectTypeDic"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <div v-if="mode === 'edit'" class="form-tip">项目类型创建后不可修改</div>
      </el-form-item>
      <el-form-item label="价盘编号">
        <el-input
          :value="mode === 'edit' ? form.code : codePreview"
          :placeholder="mode === 'edit' ? '暂无编号' : '选择项目类型和价盘类型后自动生成'"
          disabled
        />
        <div class="form-tip">
          {{ codeTip }}
        </div>
      </el-form-item>
      <el-form-item label="价盘类型" prop="priceBookType">
        <el-select
          v-model="form.priceBookType"
          placeholder="请选择价盘类型"
          style="width: 100%"
          :disabled="mode === 'edit'"
          @change="loadBaseDefaults"
        >
          <el-option
            v-for="item in priceBookTypeDic"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <div v-if="mode === 'edit'" class="form-tip">价盘类型创建后不可修改</div>
      </el-form-item>
      <el-form-item label="计价项标准价">
        <el-table :data="form.pricingItems" border stripe size="small" max-height="260">
          <el-table-column type="index" label="序号" width="55" align="center" />
          <el-table-column prop="pricingItemName" label="计价项" min-width="180" />
          <el-table-column label="标准价格" width="210" align="center">
            <template slot-scope="scope">
              <el-input-number
                v-model="scope.row.standardPrice"
                :min="0"
                :precision="2"
                controls-position="right"
                style="width: 100%"
                placeholder="未配置"
              />
            </template>
          </el-table-column>
        </el-table>
        <div class="form-tip">未配置的计价项不参与计价，费用按 0 处理</div>
      </el-form-item>
      <!-- 吊顶按型号继续保留型号价格配置入口 -->
      <el-form-item v-if="isCeiling" label="型号价格">
        <el-button size="small" icon="el-icon-setting" @click="openFilterDialog">型号标准价</el-button>
        <span v-if="filterTouched" class="filter-count">
          已暂存 {{ filterPrices.length }} 个型号标准价（保存价目表后生效）
        </span>
        <div class="form-tip">扣板、电器按产品型号分别配置标准单价；辅材不在此处配置</div>
      </el-form-item>
      <el-form-item :label="thresholdLabel" prop="threshold">
        <el-input-number
          v-model="form.threshold"
          :min="0"
          :precision="2"
          controls-position="right"
          style="width: 100%"
          :placeholder="thresholdPlaceholder"
        />
      </el-form-item>
      <el-form-item v-if="!isCeiling" label="保底补贴单价" prop="subsidyPrice">
        <el-input-number
          v-model="form.subsidyPrice"
          :min="0"
          :precision="2"
          controls-position="right"
          style="width: 100%"
          placeholder="选填，保底补贴单价（元/㎡）"
        />
      </el-form-item>
      <el-form-item label="生效开始时间" prop="effectiveStartTime">
        <el-date-picker
          v-model="form.effectiveStartTime"
          type="datetime"
          value-format="yyyy-MM-dd HH:mm:ss"
          placeholder="空 = 立即生效"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="生效结束时间" prop="effectiveEndTime">
        <el-date-picker
          v-model="form.effectiveEndTime"
          type="datetime"
          value-format="yyyy-MM-dd HH:mm:ss"
          placeholder="空 = 长期有效"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <template v-if="mode === 'edit'">
          <el-tag :type="form.status === 'ENABLE' ? 'success' : (form.status === 'DISABLE' ? 'info' : 'warning')" size="small">
            {{ form.status === 'ENABLE' ? '启用' : (form.status === 'DISABLE' ? '停用' : '草稿') }}
          </el-tag>
          <span class="form-tip" style="margin-left: 8px">启用、停用和草稿生效请在价盘列表暂存后统一提交审批</span>
        </template>
        <el-radio-group v-else v-model="form.status">
          <el-radio-button label="ENABLE">启用</el-radio-button>
          <el-radio-button label="DISABLE">停用</el-radio-button>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
    </span>

    <!-- 吊顶型号标准价明细弹窗 -->
    <el-dialog
      title="型号标准价明细"
      :visible.sync="filterDialogVisible"
      width="760px"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleFilterClosed"
    >
      <div class="form-tip" style="margin-bottom: 10px">
          每个价目表可分别维护扣板、电器的型号标准单价；保底补贴单价仅适用于扣板
      </div>
      <el-table
        v-loading="filterLoading"
        :data="filterRows"
        border
        stripe
        size="small"
        max-height="420"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="goodsName" label="所属商品" min-width="140" show-overflow-tooltip />
        <el-table-column prop="goodsFilterName" label="产品型号" min-width="120" show-overflow-tooltip />
        <el-table-column label="标准单价（元）" width="190" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.price"
              :min="0"
              :precision="2"
              controls-position="right"
              size="small"
              style="width: 100%"
              placeholder="清空 = 不参与计价"
            />
          </template>
        </el-table-column>
        <el-table-column label="保底补贴单价（仅扣板）" width="220" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-if="isCeilingPanelRow(scope.row)"
              v-model="scope.row.subsidyPrice"
              :min="0"
              :precision="2"
              controls-position="right"
              size="small"
              style="width: 100%"
              placeholder="留空 = 未配置"
            />
            <span v-else class="empty-text">不适用</span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="handleFilterClose">完成</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { projectTypeDic, priceBookTypeDic, pricingItemDic } from "@/const/work/waterProof/merchantPriceOption";
import {
  addPriceBook,
  updatePriceBook,
  getPriceBookDetail,
  findPriceBookFilterPrice,
  findPriceBookPage,
} from "@/api/work/waterProof/merchantPrice";
import { findPureWaterGoodsByRootDepartId } from "@/api/salesCloud/goodsQr";

const EMPTY_FORM = {
  name: "",
  projectType: "",
  code: "",
  priceBookType: "BASE",
  pricingItems: [],
  threshold: null,
  subsidyPrice: null,
  effectiveStartTime: null,
  effectiveEndTime: null,
  status: "ENABLE",
};

export default {
  name: "PriceBookFormDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "add", // add | edit
    },
    priceBook: {
      type: Object,
      default: null,
    },
  },
  data() {
    const validateEndTime = (rule, value, callback) => {
      if (value && this.form.effectiveStartTime && value < this.form.effectiveStartTime) {
        callback(new Error("生效结束时间不能早于开始时间"));
      } else {
        callback();
      }
    };
    return {
      projectTypeDic,
      priceBookTypeDic,
      pricingItemDic,
      dialogVisible: false,
      submitting: false,
      form: { ...EMPTY_FORM },
      // 吊顶型号基价：弹窗表格行 + 已暂存明细（filterTouched=false 时提交不带 filterPrices，后端不动已有数据）
      filterDialogVisible: false,
      filterLoading: false,
      filterRows: [],
      filterPrices: [],
      filterTouched: false,
      baseFilterPrices: {},
      baseDefaultsRequestId: 0,
      rules: {
        name: [{ required: true, message: "请输入价目表名称", trigger: "blur" }],
        projectType: [{ required: true, message: "请选择项目类型", trigger: "change" }],
        priceBookType: [{ required: true, message: "请选择价盘类型", trigger: "change" }],
        effectiveEndTime: [{ validator: validateEndTime, trigger: "change" }],
      },
    };
  },
  computed: {
    isCeiling() {
      return this.form.projectType === "HANG_CEILING";
    },
    thresholdLabel() {
      return this.isCeiling ? "面积保底阈值" : "面积保底阈值";
    },
    thresholdPlaceholder() {
      return this.isCeiling
        ? "选填，单位面积材料费上限（元/㎡）"
        : "选填，面积不足按阈值计算（㎡）";
    },
    codePreview() {
      const projectPrefix = {
        WATERPROOF: "FS",
        HANG_CEILING: "DD",
        BACKFILL: "HT",
      }[this.form.projectType];
      const typePrefix = {
        BASE: "BASE",
        STANDARD: "STD",
        ACTIVITY: "ACT",
        SPECIAL: "SALE",
      }[this.form.priceBookType];
      return projectPrefix && typePrefix ? `${projectPrefix}_${typePrefix}_000001` : "";
    },
    codeTip() {
      if (this.mode === "edit") return "价盘编号创建后不可修改";
      return this.codePreview ? `保存时自动生成，格式示例：${this.codePreview}` : "价盘编号由系统自动生成";
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val;
        if (val) this.initForm();
      },
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
    "form.projectType"(val, oldVal) {
      if (!val || val === oldVal) return;
      // 新建时切换项目类型要清掉上一项目的默认值，再加载新项目的基价。
      const configured = this.mode === "add" ? null : this.form.pricingItems;
      this.$set(this.form, "pricingItems", this.buildPricingItems(configured));
      if (this.mode === "add") {
        this.$set(this.form, "threshold", null);
        this.$set(this.form, "subsidyPrice", null);
      }
      this.loadBaseDefaults();
    },
    // 价盘类型可能由表单回显或父组件赋值，不能只依赖 el-select 的 change 事件。
    "form.priceBookType"(val, oldVal) {
      if (!val || val === oldVal) return;
      this.loadBaseDefaults();
    },
  },
  methods: {
    // 回显时间：兼容时间戳（转 "yyyy-MM-dd HH:mm:ss"）与后端返回的展示格式字符串
    toDateStr(v) {
      if (v == null || v === "") return null;
      if (typeof v === "number" || /^\d+$/.test(String(v))) {
        const d = new Date(Number(v));
        if (isNaN(d.getTime())) return null;
        const pad = (n) => (n < 10 ? "0" + n : "" + n);
        return (
          d.getFullYear() +
          "-" +
          pad(d.getMonth() + 1) +
          "-" +
          pad(d.getDate()) +
          " " +
          pad(d.getHours()) +
          ":" +
          pad(d.getMinutes()) +
          ":" +
          pad(d.getSeconds())
        );
      }
      return v;
    },
    initForm() {
      // 关闭后重新打开时，废弃上一次未完成的基价默认值请求。
      this.baseDefaultsRequestId += 1;
      if (this.mode === "edit" && this.priceBook) {
        this.form = {
          name: this.priceBook.name,
          projectType: this.priceBook.projectType,
          code: this.priceBook.code,
          priceBookType: this.priceBook.priceBookType || "BASE",
          pricingItems: this.buildPricingItems(this.priceBook.pricingItems),
          threshold: this.priceBook.threshold != null ? this.priceBook.threshold : null,
          subsidyPrice: this.priceBook.subsidyPrice != null ? this.priceBook.subsidyPrice : null,
          effectiveStartTime: this.toDateStr(this.priceBook.effectiveStartTime),
          effectiveEndTime: this.toDateStr(this.priceBook.effectiveEndTime),
          status: this.priceBook.status || "ENABLE",
        };
      } else {
        this.form = { ...EMPTY_FORM };
        this.$set(this.form, "pricingItems", this.buildPricingItems());
      }
      this.filterRows = [];
      this.filterPrices = [];
      this.filterTouched = false;
      this.baseFilterPrices = {};
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
        // 兼容父组件预填项目类型/价盘类型的场景；用户手动切换时由 watcher 和 change 事件触发。
        this.loadBaseDefaults();
      });
      if (this.mode === "edit" && this.priceBook && this.priceBook.id) {
        getPriceBookDetail(this.priceBook.id)
          .then((res) => {
            if (res && res.code === "200") {
              const detail = this.parseData(res) || {};
              this.$set(this.form, "pricingItems", this.buildPricingItems(detail.pricingItems));
            }
          })
          .catch((err) => console.log("load price book detail error:", err));
      }
    },
    /**
     * 新建非基价价盘时，用同项目类型基价作为初始展示值。
     * 查询不到基价时保持当前表单值，不阻断新价盘创建。
     */
    loadBaseDefaults() {
      if (this.mode !== "add" || !this.form.projectType || this.form.priceBookType === "BASE") {
        return;
      }
      const requestId = ++this.baseDefaultsRequestId;
      this.findBaseBook(this.form.projectType)
        .then((book) => {
          if (!book || requestId !== this.baseDefaultsRequestId) return null;
          // 详情接口通常只返回价盘主表，计价项需要再查详情；若列表已带明细则直接使用。
          if (Array.isArray(book.pricingItems) || book.pricingItems) return book;
          if (!book.id) return null;
          return getPriceBookDetail(book.id).then((res) => {
            if (!this.isSuccessResponse(res)) return null;
            const detail = this.parseData(res) || {};
            return { ...book, ...detail };
          });
        })
        .then((res) => {
          if (!res || requestId !== this.baseDefaultsRequestId || this.form.priceBookType === "BASE") return;
          const baseBook = res;
          if (!baseBook.id && !baseBook.pricingItems) return;

          const baseItems = this.buildPricingItems(baseBook.pricingItems);
          const currentItems = this.form.pricingItems || [];
          const currentByCode = {};
          currentItems.forEach((item) => {
            currentByCode[item.pricingItemCode] = item.standardPrice;
          });
          // 只填充尚未改动的空值，避免异步请求返回时覆盖用户已经修改的价格。
          const pricingItems = baseItems.map((item) => ({
            ...item,
            standardPrice:
              this.isConfiguredValue(currentByCode[item.pricingItemCode])
                ? currentByCode[item.pricingItemCode]
                : item.standardPrice,
          }));
          this.$set(this.form, "pricingItems", pricingItems);
          if (!this.isConfiguredValue(this.form.threshold) && baseBook.threshold != null) {
            this.$set(this.form, "threshold", baseBook.threshold);
          }
          if (!this.isCeiling && !this.isConfiguredValue(this.form.subsidyPrice) && baseBook.subsidyPrice != null) {
            this.$set(this.form, "subsidyPrice", baseBook.subsidyPrice);
          }
        })
        .catch((err) => console.log("load base price book defaults error:", err));
    },
    /**
     * 查找同项目类型的基价价盘。
     * 新接口支持 priceBookType 条件；旧接口或历史数据可能没有该字段，
     * 因此查不到时再查询同项目类型全部价盘并在前端按 BASE/空值筛选。
     */
    findBaseBook(projectType) {
      const query = (params) =>
        findPriceBookPage({ page: 1, size: 1000, projectType, ...params }).then((res) => {
          if (!this.isSuccessResponse(res)) return [];
          return this.extractRows(res);
        });
      return query({ priceBookType: "BASE" }).then((rows) => {
        const baseRows = rows.filter((row) => this.isBaseBook(row));
        if (baseRows.length) return this.sortBooksByCreateTime(baseRows)[0];
        return query({}).then((allRows) => {
          const fallbackRows = allRows.filter((row) => this.isBaseBook(row));
          return fallbackRows.length ? this.sortBooksByCreateTime(fallbackRows)[0] : null;
        });
      });
    },
    isSuccessResponse(res) {
      return !!res && (res.code == null || String(res.code) === "200");
    },
    // el-input-number 会把空值显示为 0；0 在价盘表单中代表未配置。
    isConfiguredValue(value) {
      if (value === null || value === undefined || value === "") return false;
      const number = Number(value);
      return !isNaN(number) && number !== 0;
    },
    extractRows(res) {
      const data = this.parseData(res);
      if (Array.isArray(data)) return data;
      if (!data || typeof data !== "object") return [];
      const candidates = [data.rows, data.data, data.records, data.content, data.list, data.items];
      for (let i = 0; i < candidates.length; i += 1) {
        if (Array.isArray(candidates[i])) return candidates[i];
      }
      return [];
    },
    isBaseBook(book) {
      if (!book) return false;
      const type = String(book.priceBookType || "").toUpperCase();
      // 空值兼容新增字段前的历史价盘；BASE_PRICE 兼容早期试运行数据。
      return !type || type === "BASE" || type === "BASE_PRICE";
    },
    sortBooksByCreateTime(rows) {
      return rows.slice().sort((a, b) => {
        const aTime = this.toTime(a && a.createTime);
        const bTime = this.toTime(b && b.createTime);
        if (aTime !== bTime) return bTime - aTime;
        return String((b && b.id) || "").localeCompare(String((a && a.id) || ""));
      });
    },
    toTime(v) {
      if (v == null || v === "") return 0;
      if (typeof v === "number" || /^\d+$/.test(String(v))) return Number(v);
      const parsed = new Date(String(v).replace(/-/g, "/")).getTime();
      return isNaN(parsed) ? 0 : parsed;
    },
    buildPricingItems(configured) {
      const byCode = {};
      let list = configured;
      if (typeof list === "string") {
        try {
          list = JSON.parse(list);
        } catch (e) {
          list = [];
        }
      }
      (Array.isArray(list) ? list : []).forEach((item) => {
        let code = String(item.pricingItemCode || "").toUpperCase();
        // 兼容早期前端误写的二排编码，保存时统一使用后端稳定编码。
        if (code === "SECOND_ROW") code = "SECOND_DRAIN";
        byCode[code] = item.standardPrice != null ? item.standardPrice : item.price;
      });
      const projectType = this.form && this.form.projectType;
      const items = projectType
        ? this.pricingItemDic.filter((item) =>
          !item.projectTypes || item.projectTypes.includes(projectType))
        : [];
      return items.map((item) => ({
        pricingItemCode: item.value,
        pricingItemName: item.label,
        standardPrice: byCode[item.value] != null ? byCode[item.value] : null,
      }));
    },
    parseData(res) {
      if (!res) return {};
      // 接口可能经过统一响应包装，也可能直接返回实体/分页对象；逐层解包 JSON 字符串。
      let value = res.data !== undefined ? res.data : res;
      for (let i = 0; i < 3 && typeof value === "string"; i += 1) {
        try {
          value = JSON.parse(value);
        } catch (e) {
          return {};
        }
      }
      if (value && value.data !== undefined && !value.rows && !value.id && !value.pricingItems) {
        return this.parseData(value);
      }
      return value || {};
    },
    // 打开型号基价明细弹窗（仅吊顶）
    openFilterDialog() {
      this.filterDialogVisible = true;
      this.loadFilterRows();
    },
    // 加载扣板、电器型号（calculateType=2/3），编辑时回显已配标准价
    // 价格默认值 = 已配基价 ?? filter.customerPrice（商品型号默认价反显）
    loadFilterRows() {
      this.filterLoading = true;
      const goodsReq = findPureWaterGoodsByRootDepartId("HANG_CEILING");
      const priceReq = this.mode === "edit" && this.priceBook
        ? findPriceBookFilterPrice(this.priceBook.id)
        : (this.form.priceBookType !== "BASE"
          ? this.findBaseBook(this.form.projectType).then((book) =>
            book && book.id ? findPriceBookFilterPrice(book.id) : null)
          : Promise.resolve(null));
      Promise.all([goodsReq, priceReq])
        .then(([goodsRes, priceRes]) => {
          const configured = {};
          if (priceRes && priceRes.code === "200") {
            const list = this.parseData(priceRes) || [];
            (Array.isArray(list) ? list : []).forEach((p) => {
              configured[p.goodsFilterId] = p;
            });
          }
          const rows = [];
          if (goodsRes.code === "200") {
            const data = this.parseData(goodsRes) || [];
            (Array.isArray(data) ? data : [])
              .filter((item) => ["2", "3"].includes(String(item.calculateType)))
              .forEach((item) => {
                (item.filters || []).forEach((f) => {
                  const configuredPrice = configured[f.id];
                  const standardPrice =
                    configuredPrice != null && configuredPrice.price != null
                      ? configuredPrice.price
                      : f.customerPrice != null
                      ? f.customerPrice
                      : null;
                  // 新增型号明细时，保底补贴单价默认取该型号标准价格，仍可单独修改；
                  // 已配置的有效补贴价继续保留。
                  const configuredSubsidyPrice =
                    configuredPrice != null ? configuredPrice.subsidyPrice : null;
                  rows.push({
                    goodsFilterId: f.id,
                    goodsFilterName: f.value,
                    goodsName: item.name,
                    calculateType: item.calculateType,
                    price: standardPrice,
                    subsidyPrice:
                      String(item.calculateType) !== "2"
                        ? null
                        : configuredSubsidyPrice != null && Number(configuredSubsidyPrice) > 0
                        ? configuredSubsidyPrice
                        : standardPrice,
                  });
                });
              });
          }
          this.filterRows = rows;
        })
        .catch((err) => {
          console.log("loadFilterRows error:", err);
          this.$message.error("产品型号加载异常");
        })
        .finally(() => {
          this.filterLoading = false;
        });
    },
    // 点击「完成」：立即暂存型号基价并关闭弹窗（不等 @closed 动画，否则快速点「确定」时 filterTouched 未生效导致 filterPrices 不提交）
    handleFilterClose() {
      this.stashFilterPrices();
      this.filterDialogVisible = false;
    },
    // 弹窗通过 ESC 等其他方式关闭时的兜底暂存
    handleFilterClosed() {
      this.stashFilterPrices();
    },
    // 暂存到 filterPrices（不提交）；价格清空的型号不参与兜底
    stashFilterPrices() {
      this.filterPrices = this.filterRows
        .filter((r) => r.price !== null && r.price !== undefined && r.price !== "")
        .map((r) => ({
          goodsFilterId: r.goodsFilterId,
          price: r.price,
          subsidyPrice: this.isCeilingPanelRow(r) ? r.subsidyPrice : null,
        }));
      this.filterTouched = true;
    },
    isCeilingPanelRow(row) {
      return row && String(row.calculateType) === "2";
    },
    handleSubmit() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        this.submitting = true;
        const payload = {
          name: this.form.name,
          priceBookType: this.form.priceBookType,
          pricingItems: this.form.pricingItems,
          threshold: this.form.threshold,
          subsidyPrice: this.form.subsidyPrice,
          effectiveStartTime: this.form.effectiveStartTime,
          effectiveEndTime: this.form.effectiveEndTime,
          status: this.form.status,
        };
        // 吊顶：仅打开过型号价格明细弹窗才提交，避免编辑其他字段时覆盖已有明细
        if (this.isCeiling && this.filterTouched) {
          payload.filterPrices = this.filterPrices;
        }
        const req =
          this.mode === "edit"
            ? updatePriceBook({ id: this.priceBook.id, ...payload })
            : addPriceBook({
                ...payload,
                projectType: this.form.projectType,
              });
        req
          .then((res) => {
            if (res.code === "200") {
              this.$message.success(this.mode === "edit" ? "编辑成功" : "新建成功");
              this.dialogVisible = false;
              this.$emit("submit-success");
            } else {
              this.$message.error(res.message || "保存失败");
            }
          })
          .catch((err) => {
            console.log("PriceBookFormDialog submit error:", err);
            this.$message.error("保存异常");
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    handleClosed() {
      this.form = { ...EMPTY_FORM };
      this.filterRows = [];
      this.filterPrices = [];
      this.filterTouched = false;
      this.baseFilterPrices = {};
      this.$refs.form && this.$refs.form.clearValidate();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.filter-count {
  margin-left: 10px;
  font-size: 12px;
  color: #e6a23c;
}
</style>
